import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Stack, Chip, Skeleton, useTheme } from '@mui/material';
import { PointOfSale, RequestQuote, Payments, AccountBalance, East } from '@mui/icons-material';
import { financeWorkspaceService } from '../../services/financeWorkspaceService';
import { formatIndianNumber } from '../../utils/currency';

/**
 * Money Movement (§20) — the cash cycle as a left-to-right flow:
 * Sales Invoiced → Receivables → Collected → Bank & Cash. Every figure is
 * live from sales_invoices / invoice_payments / the GL cash balance.
 */

const ICON: Record<string, React.ReactNode> = {
  sales: <PointOfSale />, receivables: <RequestQuote />, collection: <Payments />, bank: <AccountBalance />,
};

const MoneyMovement: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    let alive = true;
    financeWorkspaceService.moneyMovement()
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData({ stages: [] }); });
    return () => { alive = false; };
  }, []);

  const statusColor = (s: string) =>
    s === 'warn' ? theme.palette.warning.main : s === 'down' ? theme.palette.error.main : theme.palette.success.main;

  return (
    <Card variant="outlined" sx={{ mt: 1 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Money Movement</Typography>
            <Typography variant="caption" color="text.secondary">
              Sales → Receivables → Collection → Bank{data?.period ? ` · ${data.period}` : ''}
            </Typography>
          </Box>
          {data?.collectionRate != null && (
            <Chip size="small" color={data.collectionRate >= 80 ? 'success' : data.collectionRate >= 50 ? 'warning' : 'default'}
              label={`${data.collectionRate}% collected this month`} />
          )}
        </Stack>

        {!data ? (
          <Stack direction="row" spacing={1}>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={110} sx={{ flex: 1 }} />)}
          </Stack>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0, overflowX: 'auto', pb: 0.5 }}>
            {(data.stages || []).map((s: any, idx: number) => {
              const col = statusColor(s.status);
              return (
                <React.Fragment key={s.key}>
                  <Box
                    onClick={() => s.route && navigate(s.route)}
                    sx={{
                      flex: '1 0 170px', minWidth: 170, cursor: s.route ? 'pointer' : 'default',
                      borderRadius: 2, p: 1.75, border: `1px solid ${theme.palette.divider}`,
                      borderLeft: `4px solid ${col}`, bgcolor: 'background.default',
                      transition: 'box-shadow .2s, transform .2s',
                      '&:hover': s.route ? { boxShadow: 3, transform: 'translateY(-2px)' } : {},
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ color: col }}>
                      {ICON[s.key]}
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>{s.label}</Typography>
                    </Stack>
                    <Typography sx={{ fontWeight: 800, fontSize: 22, mt: 0.75 }}>
                      {s.value == null ? '—' : formatIndianNumber(Number(s.value))}
                    </Typography>
                    <Typography variant="caption" sx={{ color: s.status === 'warn' ? 'warning.main' : 'text.secondary' }} noWrap>
                      {s.note}
                    </Typography>
                  </Box>
                  {idx < (data.stages.length - 1) && (
                    <Stack alignItems="center" justifyContent="center" sx={{ px: 0.5, color: 'text.disabled', alignSelf: 'center' }}>
                      <East />
                    </Stack>
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default MoneyMovement;
